import React, { ReactNode, useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useLitpulseChromeHeight } from '../hooks/useLitpulseChromeHeight';
import { LITPULSE_DIGEST_SLOT_ID } from '../constants/litpulseChrome';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Home, LogOut, Users, ShieldCheck, ShieldAlert, Crown, Settings, Bell, Heart, Menu, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { notificationsAPI } from '../api';
import LayoutV2 from './LayoutV2';
import { FeatureNavBar } from './shared/FeatureNavBar';

interface LayoutProps {
  children?: ReactNode;
}

/**
 * Layout — Conditional wrapper that switches between V1 and V2 app shell
 * based on ENABLE_APP_SHELL_UI_V2 feature flag.
 * 
 * Supports both children prop (legacy) and Outlet (React Router v6 nested routes).
 */
const Layout: React.FC<LayoutProps> = ({ children }) => {
  const isV2 = useFeatureFlag('enable_app_shell_ui_v2');
  
  // Use Outlet for nested routes, or children for legacy usage
  const content = children || <Outlet />;
  
  if (isV2) {
    return <LayoutV2>{content}</LayoutV2>;
  }
  
  return <LayoutV1>{content}</LayoutV1>;
};

/**
 * LayoutV1 — Original layout (pre-Phase UX-A)
 * When enable_home_ui_v3 is ON: renders updated header matching V3 design
 * When OFF: renders original header (backward compatible)
 */
const LayoutV1: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isV3 = useFeatureFlag('enable_home_ui_v3');
  const chromeRef = useLitpulseChromeHeight<HTMLDivElement>();

  // Fetch unread count on mount and periodically (pause when tab hidden)
  useEffect(() => {
    if (!user) return;
    
    let interval: ReturnType<typeof setInterval> | null = null;
    
    const fetchUnreadCount = async () => {
      try {
        const response = await notificationsAPI.getUnreadCount();
        setUnreadCount(response.unread_count);
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };
    
    const startPolling = () => {
      if (interval) return;
      fetchUnreadCount();
      interval = setInterval(fetchUnreadCount, 30000);
    };
    
    const stopPolling = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
    
    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        startPolling();
      }
    };
    
    // Start polling if tab is visible
    if (!document.hidden) {
      startPolling();
    }
    
    document.addEventListener('visibilitychange', handleVisibility);
    
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user]);

  const handleLogout = (): void => {
    logout();
    navigate('/login');
  };

  // Don't show navbar on landing or auth pages
  const isAuthPage = ['/', '/login', '/signup', '/verify-email', '/request-password-reset', '/reset-password'].includes(location.pathname);

  if (isAuthPage || !user) {
    return <>{children}</>;
  }

  /* ---------- V3 Header (modern, cleaner) ---------- */
  if (isV3) {
    const trialDaysLeft = (() => {
      if (!user?.trial_active || user?.has_subscription) return 0;
      const exp = (user as any).trial_expires_at || (user as any).trial_ends_at;
      if (!exp) return 0;
      return Math.max(0, Math.ceil((new Date(exp).getTime() - Date.now()) / 86400000));
    })();

    return (
      <div className="min-h-screen" style={{ background: 'var(--lp-gradient-page, #F8FAFC)' }}>
        <div ref={chromeRef} className="sticky top-0 z-50 bg-white shadow-sm">
        {/* V3 Header */}
        <div className="bg-white/80 backdrop-blur-md border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              {/* Logo + Tagline */}
              <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/library')}>
                <div className="w-8 h-8 bg-gradient-to-br from-sky-400 to-cyan-500 rounded-lg flex items-center justify-center">
                  <Heart className="w-4.5 h-4.5 text-white fill-white" />
                </div>
                <div>
                  <span className="text-lg font-bold bg-gradient-to-r from-sky-600 to-cyan-600 bg-clip-text text-transparent">
                    LitCurrent
                  </span>
                  <span className="hidden sm:block text-[11px] text-slate-400 -mt-0.5 leading-tight">
                    Stay Ahead in Medical Literature
                  </span>
                </div>
              </div>

              {/* Navigation links — desktop */}
              <nav className="hidden md:flex items-center gap-1">
                <button
                  onClick={() => navigate('/library')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === '/library'
                      ? 'text-sky-600 bg-sky-50'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                  data-testid="nav-home-btn"
                >
                  Home
                </button>
                <button
                  onClick={() => navigate('/preferences')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === '/preferences'
                      ? 'text-sky-600 bg-sky-50'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                  data-testid="nav-preferences-btn"
                >
                  Preferences
                </button>
                <button
                  onClick={() => navigate('/inbox')}
                  className={`relative px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === '/inbox'
                      ? 'text-sky-600 bg-sky-50'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                  data-testid="nav-inbox-btn"
                >
                  Inbox
                  {unreadCount > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center h-5 min-w-[20px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full"
                      data-testid="nav-inbox-badge"
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                {/* Membership label pill */}
                <button
                  onClick={() => navigate('/membership')}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors cursor-pointer ${
                    (user as any)?.trial_active
                      ? 'bg-gradient-to-r from-sky-100 to-indigo-100 text-sky-700 hover:from-sky-200 hover:to-indigo-200'
                      : user?.plan_tier === 'premium'
                        ? 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 hover:from-amber-200 hover:to-orange-200'
                        : (user as any)?.peer_verification_status === 'verified' || (user as any)?.peer_verification_status === 'verified_provisional'
                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="nav-membership-pill"
                  title="View membership options"
                >
                  {(() => {
                    if ((user as any)?.trial_active) return 'Pro Trial';
                    const isPro = user?.plan_tier === 'premium';
                    const isVerified = (user as any)?.peer_verification_status === 'verified' || (user as any)?.peer_verification_status === 'verified_provisional';
                    if (isPro && isVerified) return 'Pro Verified';
                    if (isPro) return 'Pro Member';
                    if (isVerified) return 'Free Verified';
                    return 'Free Member';
                  })()}
                </button>
                <button
                  onClick={handleLogout}
                  className="px-3 py-2 rounded-lg text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors"
                  data-testid="nav-logout-btn"
                >
                  Log Out
                </button>
                {/* Beta Admin link for admin users */}
                {user?.email && (
                  <button
                    onClick={() => navigate('/beta-admin')}
                    className="px-3 py-2 rounded-lg text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 transition-colors"
                    data-testid="nav-beta-admin-btn"
                  >
                    <Settings className="h-3.5 w-3.5 inline mr-1" />Admin
                  </button>
                )}
              </nav>

              {/* Mobile hamburger */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100"
                aria-label="Open menu"
                data-testid="nav-mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden border-t border-slate-100 bg-white px-4 py-2">
              <div className="flex flex-col gap-1">
                <button type="button" onClick={() => { navigate('/library'); setMobileMenuOpen(false); }} className="text-left px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg">Home</button>
                <button type="button" onClick={() => { navigate('/inbox'); setMobileMenuOpen(false); }} className="text-left px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg flex items-center justify-between">
                  Inbox
                  {unreadCount > 0 && (
                    <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                <button type="button" onClick={() => { navigate('/preferences'); setMobileMenuOpen(false); }} className="text-left px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg">Preferences</button>
                <button type="button" onClick={() => { navigate('/membership'); setMobileMenuOpen(false); }} className="text-left px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg">Membership</button>
                <button type="button" onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="text-left px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg">Log Out</button>
              </div>
            </div>
          )}
        </div>

        {/* Trial banner (V3 styled) */}
        {user?.trial_active && !user?.has_subscription && trialDaysLeft > 0 && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200/50 py-1.5 px-4 text-center" data-testid="trial-bar">
            <span className="text-xs text-amber-800">
              Pro trial: <strong>{trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''}</strong> remaining.{' '}
              <button onClick={() => navigate('/plan')} className="underline font-medium hover:text-amber-900">Subscribe</button>
            </span>
          </div>
        )}

        {/* Secondary navigation — feature tiles as compact tabs */}
        <FeatureNavBar variant="soft" className="bg-white/60 border-slate-100" />
        <div id={LITPULSE_DIGEST_SLOT_ID} />
        </div>

        {/* Main Content */}
        <div>
          {children}
        </div>
      </div>
    );
  }

  /* ---------- Original V1 Header (fallback) ---------- */

  return (
    <div className="min-h-screen bg-gray-50">
      <div ref={chromeRef} className="sticky top-0 z-50 bg-white shadow-sm">
      {/* Top Navigation Bar */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            {/* Logo/Brand */}
            <div className="flex items-center">
              <h1 className="text-lg font-bold text-blue-600">LitCurrent</h1>
            </div>

            {/* Navigation — desktop */}
            <div className="hidden md:flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/library')}
                className={location.pathname === '/library' || location.pathname === '/learn' || location.pathname.startsWith('/library/') ? 'bg-gray-100' : ''}
                data-testid="nav-home-btn"
              >
                <Home className="h-4 w-4 mr-1.5" />
                Home
              </Button>
              
              
              {/* Inbox button with badge */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/inbox')}
                className={`relative ${location.pathname === '/inbox' ? 'bg-gray-100' : ''}`}
                data-testid="nav-inbox-btn"
              >
                <Bell className="h-4 w-4 mr-1.5" />
                Inbox
                {unreadCount > 0 && (
                  <Badge 
                    className="absolute -top-1 -right-1 h-5 min-w-[20px] flex items-center justify-center p-0 text-[10px] bg-red-500 text-white border-0"
                    data-testid="nav-inbox-badge"
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </Badge>
                )}
              </Button>
              
              {/* Settings dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="nav-settings-btn"
                  >
                    <Settings className="h-4 w-4 mr-1.5" />
                    Settings
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate('/preferences')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Preferences
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/plan')}>
                    <Crown className="h-4 w-4 mr-2" />
                    Plan
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/verification')}>
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Professional Verification
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/phi-guidelines')}>
                    <ShieldAlert className="h-4 w-4 mr-2" />
                    PHI-Zero Policy
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="nav-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-1.5" />
                Logout
              </Button>
            </div>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100"
              aria-label="Open menu"
              data-testid="nav-mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden border-t border-slate-200 bg-white px-4 py-2">
              <div className="flex flex-col gap-1">
                <button type="button" onClick={() => { navigate('/library'); setMobileMenuOpen(false); }} className="text-left px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg">Home</button>
                <button type="button" onClick={() => { navigate('/inbox'); setMobileMenuOpen(false); }} className="text-left px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg flex items-center justify-between">
                  Inbox
                  {unreadCount > 0 && (
                    <Badge className="h-5 min-w-[20px] flex items-center justify-center p-0 text-[10px] bg-red-500 text-white border-0">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                  )}
                </button>
                <button type="button" onClick={() => { navigate('/preferences'); setMobileMenuOpen(false); }} className="text-left px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg">Preferences</button>
                <button type="button" onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="text-left px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg">Logout</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Trial banner */}
      {user?.trial_active && !user?.has_subscription && (() => {
        const daysLeft = user.trial_expires_at
          ? Math.max(0, Math.ceil((new Date(user.trial_expires_at).getTime() - Date.now()) / 86400000))
          : 0;
        return (
          <div className="bg-amber-50 border-b border-amber-200 py-1.5 px-4 text-center" data-testid="trial-bar">
            <span className="text-xs text-amber-800">
              Pro trial: <strong>{daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong> remaining.{' '}
              <button onClick={() => navigate('/plan')} className="underline font-medium hover:text-amber-900">Subscribe</button>
            </span>
          </div>
        );
      })()}

      {/* Secondary Navigation — personalized feature links */}
      <FeatureNavBar className="bg-gradient-to-r from-sky-50/80 via-slate-50/60 to-white border-slate-200/60" />
      <div id={LITPULSE_DIGEST_SLOT_ID} />
      </div>

      {/* Main Content */}
      <div>
        {children}
      </div>
    </div>
  );
};

export default Layout;
